import './BreadcrumbBar.css';

import Breadcrumbs from '@mui/material/Breadcrumbs';
import React from 'react';
import Typography from '@mui/material/Typography';
import { Link as RouterLink, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRtuDetailChrome } from '../../../context/RtuDetailChromeContext';

interface BreadcrumbItem {
  label: string;
  path?: string;
}

interface BreadcrumbBarProps {
  items?: BreadcrumbItem[];
}

const BreadcrumbBar: React.FC<BreadcrumbBarProps> = ({ items }) => {
  const { pathname } = useLocation();
  const { rtuId } = useParams();
  const { t } = useTranslation();
  const { breadcrumbTrailingSlot, rtuBreadcrumbLabel } = useRtuDetailChrome();
  const showDeviceDetailTrailing = pathname.startsWith('/rtupage/') && Boolean(breadcrumbTrailingSlot);

  const derivedItems = React.useMemo(() => {
    if (items?.length) {
      return items;
    }

    if (pathname === '/' || pathname === '/dashboard') {
      return [{ label: t('breadcrumb.dashboard') }];
    }

    if (pathname === '/rtulist') {
      return [
        { label: t('breadcrumb.assets'), path: '/rtulist' },
        { label: t('breadcrumb.assetList') },
      ];
    }

    if (pathname === '/rtutreelist') {
      return [
        { label: t('breadcrumb.assets'), path: '/rtutreelist' },
        { label: t('breadcrumb.groupedAssets') },
      ];
    }

    if (pathname.startsWith('/rtupage/')) {
      const detailLabel = rtuBreadcrumbLabel?.trim()
        || (rtuId ? t('breadcrumb.assetDetailsWithId', { value: rtuId }) : t('breadcrumb.assetDetails'));

      return [
        { label: t('breadcrumb.assets'), path: '/rtulist' },
        { label: t('breadcrumb.assetList'), path: '/rtulist' },
        { label: detailLabel },
      ];
    }

    if (pathname === '/mapview') {
      return [
        { label: t('breadcrumb.assets'), path: '/rtulist' },
        { label: t('breadcrumb.mapView', { defaultValue: t('navigation.mapView') }) },
      ];
    }

    if (pathname === '/notifications') {
      return [{ label: t('breadcrumb.notifications') }];
    }

    if (pathname === '/actions') {
      return [{ label: t('actionsWorkbench.title', { defaultValue: 'All Actions' }) }];
    }

    if (pathname === '/audit/actions') {
      return [{ label: t('audit.actions.title', { defaultValue: 'Actions History' }) }];
    }

    if (pathname === '/audit/report') {
      return [{ label: t('audit.report.title', { defaultValue: 'Reports' }) }];
    }

    if (pathname === '/observability/ingestion') {
      return [{ label: t('breadcrumb.ingestionObservability') }];
    }

    if (pathname === '/admin/ping-settings' || pathname.startsWith('/admin/settings')) {
      const masterSettings = t('navigation.masterSettings', { defaultValue: 'Master Settings' });
      const masterPath = '/admin/settings/reachability/ping-templates';

      if (pathname.includes('/system-defaults/storage-attention') || pathname.endsWith('/dashboard-defaults')) {
        return [
          { label: masterSettings, path: masterPath },
          { label: 'System Defaults', path: '/admin/settings/system-defaults/storage-attention' },
          { label: 'Storage Attention' },
        ];
      }

      if (pathname.includes('/reachability') || pathname === '/admin/ping-settings') {
        return [
          { label: masterSettings, path: masterPath },
          { label: 'Reachability', path: masterPath },
          { label: 'Ping templates' },
        ];
      }

      if (pathname.includes('/retention/policies')) {
        return [
          { label: masterSettings, path: masterPath },
          { label: 'Data Lifecycle', path: '/admin/settings/retention/policies' },
          { label: 'Retention policies' },
        ];
      }

      return [{ label: masterSettings }];
    }

    return [{ label: t('breadcrumb.dashboard'), path: '/dashboard' }];
  }, [items, pathname, rtuBreadcrumbLabel, rtuId, t]);

  return (
    <nav
      className={`breadcrumb-bar${showDeviceDetailTrailing ? ' breadcrumb-bar--with-trailing' : ''}`}
      aria-label={t('breadcrumb.ariaLabel')}
    >
      <div className="breadcrumb-bar__crumbs">
        <Breadcrumbs separator=">" maxItems={4}>
          {derivedItems.map((item, index) => {
            const isLast = index === derivedItems.length - 1 || !item.path;

            if (isLast) {
              return (
                <Typography key={`${item.label}-${index}`} className="breadcrumb-bar__current" aria-current="page">
                  {item.label}
                </Typography>
              );
            }

            return (
              <RouterLink
                key={`${item.label}-${index}`}
                to={item.path!}
                className="breadcrumb-bar__link"
              >
                {item.label}
              </RouterLink>
            );
          })}
        </Breadcrumbs>
      </div>
      {showDeviceDetailTrailing ? (
        <div className="breadcrumb-bar__trailing">{breadcrumbTrailingSlot}</div>
      ) : null}
    </nav>
  );
};

export default BreadcrumbBar;
